import React from 'react';
import useQuerySummarizeMutation from './useQuerySummarizeMutation';

interface HookResult {
  text: string | null;
  mutate(sessionId: string): void;
  loading: boolean;
  error: string | null;
}

export default function useStreamText(onEnd: (text: string) => void): HookResult {
  const [text, setText] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const mutation = useQuerySummarizeMutation();
  const mutate = React.useCallback((sessionId: string) => {
    setText('');
    setError(null);

    mutation.mutate({ sessionId }, {
      onSuccess: async (reader) => {
        const decoder = new TextDecoder();
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          let startIdx = buffer.indexOf('{"text":');
          while (startIdx !== -1 || buffer.includes('{"error":')) {
            const endIdx = buffer.indexOf('}', startIdx !== -1 ? startIdx : 0);
            if (endIdx === -1) break;

            const jsonString = buffer.slice(0, endIdx + 1);
            buffer = buffer.slice(endIdx + 1);

            try {
              const parsed = JSON.parse(jsonString);

              if (parsed.error) {
                setError(parsed.error);
                return;
              }

              if (parsed.text) {
                setText((prevText) => prevText + parsed.text);
              }
            } catch (err) {
              console.error('Error parsing JSON chunk:', jsonString, err);
            }

            startIdx = buffer.indexOf('{"text":');
          }
        }

        onEnd(text ?? '');
      },
      onError: (err) => {
        console.error('Error during streaming:', err);
        setError(err.message || 'An unknown error occurred.');
      },
    });
  }, [mutation, onEnd]);

  return {
    text,
    mutate,
    loading: mutation.isLoading,
    error,
  };
}
